import { __ } from '@wordpress/i18n';
import { createYearSelect } from './yearSelect';

const REST_BASE = '/wp-json/anacrm/v1';

function nonce() {
	return window.anacrm?.nonce ?? '';
}

async function apiFetch( url, options = {} ) {
	const res = await fetch( url, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': nonce(),
			...( options.headers ?? {} ),
		},
	} );
	if ( res.status === 204 ) {
		return null;
	}
	const body = await res.json().catch( () => ( {} ) );
	if ( ! res.ok ) {
		// eslint-disable-next-line no-console
		console.error( '[size] apiFetch error', res.status, url, body );
		throw new Error( body?.message ?? `HTTP ${ res.status }` );
	}
	return body;
}

function sizeLabel( item ) {
	const size = Number( item.size ?? 0 ).toLocaleString( 'es-ES' );
	return `${ item.year } — ${ size }`;
}

function renderRow( container, item, onDelete, onEdit ) {
	const row = document.createElement( 'div' );
	row.className = 'flex items-center gap-2 py-1 size-row';
	row.dataset.id = item.id ?? '';

	const label = document.createElement( 'span' );
	label.className = 'flex-1 text-sm';
	label.textContent = sizeLabel( item );

	const edit = document.createElement( 'button' );
	edit.type = 'button';
	edit.className =
		'px-2 py-0.5 text-sm text-gray-600 hover:text-gray-900 cursor-pointer shrink-0';
	edit.textContent = __( 'Edit', 'anacrm' );
	edit.addEventListener( 'click', () => onEdit( row, label, item ) );

	const del = document.createElement( 'button' );
	del.type = 'button';
	del.className =
		'px-2 py-0.5 text-sm text-red-600 hover:text-red-800 cursor-pointer shrink-0';
	del.textContent = __( 'Remove', 'anacrm' );
	del.addEventListener( 'click', () => onDelete( row, item ) );

	row.appendChild( label );
	row.appendChild( edit );
	row.appendChild( del );
	container.appendChild( row );
}

/**
 * Mount the "Size" repeater into `container`. Three subsections — total,
 * insertion and disabilities — each with its own add/edit/delete flow
 * (year + size, no category picker). Follows the same isNew/pending-hidden-
 * input pattern as initOrgSolvencySection() in solvencyRepeater.js; since
 * all three subsections share the same shape (unlike solvency's financial/
 * technical split), they're built by one shared buildSubsection() closure
 * instead of being duplicated per type.
 *
 * @param {HTMLElement} container Mount point (inside the org form).
 * @param {string}      orgId     Post ID string, empty for new-org form.
 * @param {boolean}     isNew     True when creating a new org.
 */
export async function initOrgSizeSection( container, orgId, isNew ) {
	const intro = document.createElement( 'p' );
	intro.className = 'text-sm mb-3';
	intro.textContent = __(
		'Number of people, by year: total size of the organization, people in employment insertion processes, and people with disabilities.',
		'anacrm'
	);
	container.appendChild( intro );

	// Pending list for new-org form (not yet persisted). Shared between the
	// three subsections, each entry tagged by `type`.
	let pending = [];

	const hiddenInput = document.createElement( 'input' );
	hiddenInput.type = 'hidden';
	hiddenInput.name = 'anacrm_pending_sizes';
	hiddenInput.value = '[]';
	container.appendChild( hiddenInput );

	function syncHidden() {
		hiddenInput.value = JSON.stringify( pending );
	}

	let items = [];
	if ( ! isNew && orgId ) {
		try {
			const fetched = await apiFetch(
				`${ REST_BASE }/org/${ orgId }/sizes`
			);
			if ( Array.isArray( fetched ) ) {
				items = fetched;
			}
		} catch {
			// Non-fatal — show empty lists.
		}
	}

	async function handleDelete( row, item ) {
		if ( ! item.id ) {
			return;
		}
		try {
			await apiFetch( `${ REST_BASE }/sizes/${ item.id }`, {
				method: 'DELETE',
			} );
			row.remove();
		} catch {
			const e = document.createElement( 'span' );
			e.className = 'text-xs text-red-600 ml-1';
			e.textContent = __( 'Error', 'anacrm' );
			row.appendChild( e );
		}
	}

	function handleDeletePending( row, item ) {
		pending = pending.filter( ( p ) => p !== item );
		syncHidden();
		row.remove();
	}

	function buildSubsection( type, label ) {
		const wrap = document.createElement( 'div' );
		wrap.className = 'mb-6';
		container.appendChild( wrap );

		const wrapLabel = document.createElement( 'p' );
		wrapLabel.className = 'text-sm font-medium mb-1';
		wrapLabel.textContent = label;
		wrap.appendChild( wrapLabel );

		const list = document.createElement( 'div' );
		list.className = 'size-list mb-3 space-y-1';
		wrap.appendChild( list );

		for ( const item of items.filter( ( i ) => type === i.type ) ) {
			renderRow( list, item, handleDelete, startEdit );
		}

		const addArea = document.createElement( 'div' );
		wrap.appendChild( addArea );

		const addBtn = document.createElement( 'button' );
		addBtn.type = 'button';
		addBtn.className =
			'text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 cursor-pointer';
		addBtn.textContent = `+ ${ __(
			'Add',
			'anacrm'
		) } ${ label.toLowerCase() }`;
		addArea.appendChild( addBtn );

		const form = document.createElement( 'div' );
		form.className =
			'hidden flex flex-wrap gap-2 mt-2 items-end size-add-form';
		addArea.appendChild( form );

		const yearInput = createYearSelect(
			'border border-gray-300 rounded px-2 py-1 text-sm w-24'
		);

		const sizeInput = document.createElement( 'input' );
		sizeInput.type = 'number';
		sizeInput.min = '0';
		sizeInput.step = '1';
		sizeInput.placeholder = __( 'Number of people', 'anacrm' );
		sizeInput.className =
			'border border-gray-300 rounded px-2 py-1 text-sm w-40';

		const save = document.createElement( 'button' );
		save.type = 'button';
		save.className =
			'px-3 py-1 text-sm bg-gray-800 text-white rounded hover:bg-gray-700 cursor-pointer shrink-0';
		save.textContent = __( 'Save', 'anacrm' );

		const cancel = document.createElement( 'button' );
		cancel.type = 'button';
		cancel.className =
			'px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 cursor-pointer shrink-0';
		cancel.textContent = __( 'Cancel', 'anacrm' );

		const err = document.createElement( 'p' );
		err.className = 'w-full text-xs text-red-600 hidden';

		form.appendChild( yearInput );
		form.appendChild( sizeInput );
		form.appendChild( save );
		form.appendChild( cancel );
		form.appendChild( err );

		let editing = null;

		function startEdit( row, rowLabel, item ) {
			editing = { row, label: rowLabel, item };
			addBtn.classList.add( 'hidden' );
			form.classList.remove( 'hidden' );
			yearInput.value = item.year ?? '';
			sizeInput.value = item.size ?? '';
			err.classList.add( 'hidden' );
			save.textContent = __( 'Update', 'anacrm' );
		}

		function stopEdit() {
			editing = null;
			yearInput.value = '';
			sizeInput.value = '';
			form.classList.add( 'hidden' );
			addBtn.classList.remove( 'hidden' );
			save.textContent = __( 'Save', 'anacrm' );
		}

		addBtn.addEventListener( 'click', () => {
			addBtn.classList.add( 'hidden' );
			form.classList.remove( 'hidden' );
			yearInput.value = '';
			sizeInput.value = '';
			err.classList.add( 'hidden' );
		} );

		cancel.addEventListener( 'click', stopEdit );

		save.addEventListener( 'click', async () => {
			const year = yearInput.value.trim();
			const size = sizeInput.value.trim();

			if (
				! /^\d{4}$/.test( year ) ||
				'' === size ||
				isNaN( Number( size ) )
			) {
				err.textContent = __(
					'Please enter a valid year and number of people.',
					'anacrm'
				);
				err.classList.remove( 'hidden' );
				return;
			}

			err.classList.add( 'hidden' );
			save.disabled = true;

			if ( editing ) {
				const { label: rowLabel, item } = editing;
				if ( item.id ) {
					try {
						const updated = await apiFetch(
							`${ REST_BASE }/sizes/${ item.id }`,
							{
								method: 'PUT',
								body: JSON.stringify( {
									org_id: parseInt( orgId, 10 ),
									type,
									year,
									size: Number( size ),
								} ),
							}
						);
						Object.assign( item, updated );
						rowLabel.textContent = sizeLabel( item );
						stopEdit();
					} catch ( err2 ) {
						// eslint-disable-next-line no-console
						console.error( '[size] update failed', err2 );
						err.textContent =
							err2.message ||
							__( 'Could not save. Please try again.', 'anacrm' );
						err.classList.remove( 'hidden' );
					} finally {
						save.disabled = false;
					}
				} else {
					item.year = year;
					item.size = Number( size );
					syncHidden();
					rowLabel.textContent = sizeLabel( item );
					stopEdit();
					save.disabled = false;
				}
				return;
			}

			if ( isNew ) {
				const entry = { type, year, size: Number( size ) };
				pending.push( entry );
				syncHidden();
				renderRow( list, entry, handleDeletePending, startEdit );
				stopEdit();
				save.disabled = false;
			} else {
				try {
					const created = await apiFetch( `${ REST_BASE }/sizes`, {
						method: 'POST',
						body: JSON.stringify( {
							org_id: parseInt( orgId, 10 ),
							type,
							year,
							size: Number( size ),
						} ),
					} );
					renderRow( list, created, handleDelete, startEdit );
					stopEdit();
				} catch ( err2 ) {
					// eslint-disable-next-line no-console
					console.error( '[size] save failed', err2 );
					err.textContent =
						err2.message ||
						__( 'Could not save. Please try again.', 'anacrm' );
					err.classList.remove( 'hidden' );
				} finally {
					save.disabled = false;
				}
			}
		} );
	}

	buildSubsection( 'total', __( 'Total size', 'anacrm' ) );
	buildSubsection( 'insertion', __( 'Insertion', 'anacrm' ) );
	buildSubsection( 'disabilities', __( 'Disabilities', 'anacrm' ) );
}
